import { createSelector } from 'reselect';

import {
  BearingEnvelope,
  FeaturePatternTrussContainer,
  FloorPolyline3D,
  FloorPolylineSegmentGeometry,
} from '@paragon/contract-library';
import {
  angleGreaterThanOrEqual,
  angleLessThanOrEqual,
  convertDegreesToRadians,
  Direction3D,
  distanceLessThanOrEqual,
  Line2D,
  LineSegment2D,
  Plane3D,
  Polygon2D,
  Polyhedron,
  rightAngle,
} from '@paragon/geometry-library';
import { Rotation } from '@paragon/geometry-library/source/scripts/rotation';
import { assertExhaustiveSwitch, notNullish, ReadonlyRecord } from '@paragon/utilities';

import { absoluteVerticalCutToPlane, cutPlaneByOrderedPlanes } from './roof-planes';
import { getEnvelopeSegmentFromEnvelope } from './thickness-calculations';

export const calculateFeaturePatternContainer3DShapeWithCache = createSelector(
  (featurePatternContainer: Omit<FeaturePatternTrussContainer, 'guid'>) => featurePatternContainer,
  (
    featurePatternContainer: Omit<FeaturePatternTrussContainer, 'guid'>,
    bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
  ) => bearingEnvelopes,
  calculateFeaturePatternContainer3DShape,
);

/** Calculates the solid defined by `source`. On failure, returns null. */
function calculateFeaturePatternContainer3DShape(
  source: Omit<FeaturePatternTrussContainer, 'guid'>,
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
): Polyhedron | null {
  const in2D = calculateFloorPolyline2DShape(source.shape, bearingEnvelopes);
  if (in2D == null) return null; // Invalid cuts

  const deltaElevation = source.shape.topElevation - source.shape.bottomElevation;
  if (distanceLessThanOrEqual(deltaElevation, 0)) return null;

  if (!source.slopeSettings) {
    // The common case: unsloped container
    return in2D.to3D(source.shape.bottomElevation).extrude(Direction3D.positiveZ.toVector(deltaElevation));
  }

  const { slopeSettings } = source;
  if (
    [slopeSettings.topSlopeInDegrees, slopeSettings.bottomSlopeInDegrees].some(
      (angle) => angleGreaterThanOrEqual(angle, rightAngle) || angleLessThanOrEqual(angle, -rightAngle),
    )
  )
    return null; // Invalid slopes

  const edgeToSlopeFrom = in2D.edges.find((e) =>
    e.overlaps(LineSegment2D.fromLineSegmentObject(slopeSettings.edgeToSlopeFrom)),
  );
  if (!edgeToSlopeFrom) return null; // Invalid edge to slope from

  const topChordSlopeCoefficient = Math.tan(convertDegreesToRadians(slopeSettings.topSlopeInDegrees));
  const bottomChordSlopeCoefficient = Math.tan(convertDegreesToRadians(slopeSettings.bottomSlopeInDegrees));
  const slopeAxis = new Line2D(edgeToSlopeFrom.midpoint, edgeToSlopeFrom.direction.rotateQuarterTurn());
  const pairedPoints = in2D.vertices.map((v) => {
    const projection = v.projectedSignedDistanceAlong(slopeAxis);
    return {
      top: projection * topChordSlopeCoefficient + source.shape.topElevation,
      bottom: projection * bottomChordSlopeCoefficient + source.shape.bottomElevation,
    };
  });
  if (pairedPoints.some(({ top, bottom }) => distanceLessThanOrEqual(top, bottom))) return null; // Slopes lead to self-intersecting container

  const highestElevation = Math.max(...pairedPoints.map(({ top }) => top));
  const lowestElevation = Math.min(...pairedPoints.map(({ bottom }) => bottom));

  const unslopedContainer = in2D
    .to3D(lowestElevation)
    .extrude(Direction3D.positiveZ.toVector(highestElevation - lowestElevation));

  const topSlice = Plane3D.fromPerpendicularDirectionAndPoint(
    Direction3D.negativeZ.rotate(
      Rotation.fromAxisAndAngle(edgeToSlopeFrom.to3D().toLine(), slopeSettings.topSlopeInDegrees),
    ),
    edgeToSlopeFrom.midpoint.to3D(source.shape.topElevation),
  );
  const bottomSlice = Plane3D.fromPerpendicularDirectionAndPoint(
    Direction3D.positiveZ.rotate(
      Rotation.fromAxisAndAngle(edgeToSlopeFrom.to3D().toLine(), slopeSettings.bottomSlopeInDegrees),
    ),
    edgeToSlopeFrom.midpoint.to3D(source.shape.bottomElevation),
  );

  return unslopedContainer.trimByMany([topSlice, bottomSlice])[0] ?? null;
}

/**
 * Calculates the polygonal boundary defined by `source`. On failure, returns as many segments and lines as could be
 * calculated, or null.
 */
export function calculateFloorPolyline2DShape(
  source: FloorPolyline3D,
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
): Polygon2D | null {
  if (source.geometry.length === 0) return null;

  const sourceGeometry = Plane3D.fromPerpendicularDirectionAndPoint(Direction3D.positiveZ);
  const planesToCutAgainst = source.geometry.map((cut) => containerCutToPlane(cut, bearingEnvelopes));
  if (planesToCutAgainst.every(notNullish)) {
    const regions = cutPlaneByOrderedPlanes(sourceGeometry, planesToCutAgainst, false);
    if (!regions.inProgress) return regions.shape.length === 1 ? regions.shape[0].projectTo2D()! : null;
  }

  return null;
}

export function floorPolylineSegmentToLineSegment(
  segment: FloorPolylineSegmentGeometry,
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
): LineSegment2D | null {
  switch (segment.type) {
    case 'AbsoluteVertical':
      return LineSegment2D.fromLineSegmentObject(segment.segment);
    case 'AgainstBearing':
      const bearingEnvelope = bearingEnvelopes[segment.bearingEnvelopeGuid];
      if (!bearingEnvelope) return null;
      return getEnvelopeSegmentFromEnvelope(bearingEnvelope, segment.flipped ? 'Back' : 'Front');
    default:
      assertExhaustiveSwitch(segment);
  }
}

function containerCutToPlane(
  cut: FloorPolyline3D['geometry'][number],
  bearingEnvelopes: ReadonlyRecord<string, BearingEnvelope>,
): Plane3D | null {
  const segment = floorPolylineSegmentToLineSegment(cut, bearingEnvelopes);
  return segment && absoluteVerticalCutToPlane({ type: 'AbsoluteVertical', segment, isInfinite: false });
}
